import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import hospitalApiService from '../../services/hospitalApiService';
import ProcLinkTemplate from './ProcLinkTemplate';
import DeptLinkTemplate from './DeptLinkTemplate';
import HospitalLinkTemplate from './HospitalLinkTemplate';

type SlugType = 'hospital' | 'department' | 'procedure' | null;

function detectType(data: any): SlugType {
  if (!data) return null;
  // API returns explicit type field
  if (data.type === 'hospital') return 'hospital';
  if (data.type === 'department') return 'department';
  if (data.type === 'procedure') return 'procedure';
  // Fallback: detect from response shape
  if (data.hospital && data.departments) return 'hospital';
  if (data.procedures && data.settings) return 'department';
  if (data.procedure || data.videos || data.pricing) return 'procedure';
  if (data.settings) return 'procedure';
  return null;
}

export default function SlugResolver() {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<any>(null);
  const [type, setType] = useState<SlugType>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!slug) return;
    hospitalApiService.getPreviewBySlug(slug)
      .then((res) => {
        const d = res.data;
        setData(d);
        setType(detectType(d));
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <Box sx={{ bgcolor: '#F6F8FB', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#1A7F78' }} />
      </Box>
    );
  }

  if (error || !data || !type) {
    return (
      <Box sx={{ bgcolor: '#F6F8FB', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
        <Typography fontSize={48}>&#128533;</Typography>
        <Typography sx={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 700, color: '#0C1F35' }}>
          Link not found
        </Typography>
        <Typography sx={{ color: '#8FA5B8', fontSize: 14 }}>
          This page may have been moved or is no longer available.
        </Typography>
      </Box>
    );
  }

  switch (type) {
    case 'hospital':
      return <HospitalLinkTemplate initialData={data} />;
    case 'department':
      return <DeptLinkTemplate initialData={data} />;
    case 'procedure':
      return <ProcLinkTemplate initialData={data} />;
    default:
      return null;
  }
}
